/**
 * Single source of truth for the demo data shown in every app-UI visual.
 *
 * Every visual on the site depicts the same fictional Dutch accountancy firm, so a client, a
 * colleague or a planning cell must look identical wherever it appears. Before this module the
 * data was copy-pasted per visual and had drifted (the same client number used for two different
 * clients, a KvK publication marked done while its annual accounts were still in progress, avatar
 * initials with nobody behind them). Import from here instead of hardcoding.
 *
 * Content strings are Dutch because they render inside a mock of the Dutch app UI; identifiers and
 * comments stay English per the repo language policy.
 *
 * Domain rules encoded below (they mirror how Dutch compliance work actually sequences):
 * - Only legal entities (BV / Holding / SRL) file vennootschapsbelasting; a natural-person business
 *   (eenmanszaak / VOF) files inkomstenbelasting instead — never both.
 * - `KvK-publicatie` can only be running or done once that client's `Jaarrekening` is done: you
 *   cannot deposit annual accounts that have not been adopted yet.
 * - A VOF or eenmanszaak never publishes at the KvK, and a Belgian SRL deposits with the NBB
 *   rather than the Dutch KvK — both render as an empty cell.
 */

/* ------------------------------------------------------------------ team */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();export type TeamMember = {
  /** Avatar initials — also the id used across visuals. */
  initials: string
  name: string
  /** First name, for chat and notification copy ("door Anouk"). */
  firstName: string
  /** Job title, as a key into the copy tables — a Dutch firm's roles still read in English. */
  role: TeamRoleKey
  /** Avatar background. */
  color: string
}

/** The job titles on this firm's staff list. Written out per language in the visual copy. */
export type TeamRoleKey =
  'accountant' | 'relatiebeheerder' | 'assistentAccountant' | 'salarisadministrateur' | 'administrateur'

/**
 * The firm's staff. A mid-size Dutch accountancy practice runs a mixed team: a couple of
 * accountants who review and sign off, assistants who prepare the work, administrators who do the
 * bookkeeping, and a payroll specialist.
 */
export const TEAM = [
  { initials: 'MV', name: 'Mark Verhoeven', firstName: 'Mark', role: 'accountant', color: '#4c7fd0' },
  { initials: 'SV', name: 'Sanne Visser', firstName: 'Sanne', role: 'relatiebeheerder', color: '#3f6fd8' },
  { initials: 'AS', name: 'Anouk Smit', firstName: 'Anouk', role: 'assistentAccountant', color: '#16a08c' },
  { initials: 'TV', name: 'Thijs van den Berg', firstName: 'Thijs', role: 'assistentAccountant', color: '#2d8cff' },
  { initials: 'YE', name: 'Yasmine El Amrani', firstName: 'Yasmine', role: 'salarisadministrateur', color: '#c2408a' },
  { initials: 'DA', name: 'Daan Akkerman', firstName: 'Daan', role: 'administrateur', color: '#7a5cd0' },
  { initials: 'HM', name: 'Hidde Mulder', firstName: 'Hidde', role: 'administrateur', color: '#c2703f' },
  { initials: 'NK', name: 'Nina Kuiper', firstName: 'Nina', role: 'assistentAccountant', color: '#2f9e7d' },
  { initials: 'LD', name: 'Lisa Dekker', firstName: 'Lisa', role: 'administrateur', color: '#b8437c' },
  { initials: 'ID', name: 'Iris Dijkstra', firstName: 'Iris', role: 'assistentAccountant', color: '#3d7ea6' },
  { initials: 'BJ', name: 'Bram Jonker', firstName: 'Bram', role: 'administrateur', color: '#8a6a3f' },
] as const satisfies readonly TeamMember[]

export type TeamInitials = (typeof TEAM)[number]['initials']

const TEAM_BY_INITIALS = new Map(TEAM.map((member) => [member.initials as string, member as TeamMember]))

/** Looks a colleague up by initials; throws in development if the initials are unknown. */
export function teamMember(initials: TeamInitials): TeamMember {
  const member = TEAM_BY_INITIALS.get(initials)
  if (!member) throw new Error(`Unknown team member: ${initials}`)
  return member
}

/** Avatar background per initials, for `AvatarStack` and inline avatars. */
export const AVATAR_COLORS: Record<string, string> = Object.fromEntries(
  TEAM.map((member) => [member.initials, member.color]),
)

/* --------------------------------------------------------------- clients */

/** Legal form, rendered as the small uppercase tag behind a client name. */
export type LegalForm = 'HOLDING' | 'BV' | 'EENMANSZAAK' | 'VOF' | 'SRL' | 'STICHTING'

export type Client = {
  /** Client number — unique, and stable across every visual. */
  nr: string
  name: string
  /** Shortened label for narrow columns (mobile, organogram nodes). */
  short: string
  form: LegalForm
  /** The colleague who owns the relationship — a column on the client list. */
  manager: TeamInitials
  /** Client number of the parent holding, when this entity sits in a group. */
  parent?: string
}

/**
 * The client base. The legal-form mix mirrors a real Dutch practice, where the BV dominates and
 * holdings, eenmanszaken and VOFs follow at a distance.
 *
 * Numbering is stable and grouped: a holding and its subsidiaries sit in one consecutive block, so
 * an indented row in a planning board is always the entity directly above it.
 */
export const CLIENTS = [
  // Van Dijk group — the holding structure shown on the year-planning board.
  { nr: '10001', name: 'Van Dijk Beheer Holding B.V.', short: 'Van Dijk Beheer', manager: 'DA', form: 'HOLDING' },
  {
    nr: '10002',
    name: 'Van Dijk Vastgoed B.V.',
    short: 'Van Dijk Vastgoed',
    manager: 'DA',
    form: 'BV',
    parent: '10001',
  },
  {
    nr: '10003',
    name: 'Van Dijk Installaties B.V.',
    short: 'Van Dijk Installaties',
    manager: 'DA',
    form: 'BV',
    parent: '10001',
  },

  { nr: '10010', name: 'Noorderpoort IT B.V.', short: 'Noorderpoort IT', manager: 'HM', form: 'BV' },
  { nr: '10014', name: 'De Haan Logistiek B.V.', short: 'De Haan Logistiek', manager: 'HM', form: 'BV' },
  { nr: '10018', name: 'Bakkerij Vermeulen', short: 'Bakkerij Vermeulen', manager: 'YE', form: 'EENMANSZAAK' },
  { nr: '10020', name: 'Oosterrijck Media Holding B.V.', short: 'Oosterrijck Media', manager: 'MV', form: 'HOLDING' },
  { nr: '10021', name: 'Fysio Middenland', short: 'Fysio Middenland', manager: 'NK', form: 'EENMANSZAAK' },
  { nr: '10024', name: 'Lindehof Vastgoed B.V.', short: 'Lindehof Vastgoed', manager: 'TV', form: 'BV' },
  { nr: '10027', name: 'Peeters & Partners', short: 'Peeters & Partners', manager: 'DA', form: 'VOF' },
  { nr: '10031', name: 'Scheldehoek Trading SRL', short: 'Scheldehoek Trading', manager: 'SV', form: 'SRL' },

  // Rivierwijk group — the structure shown in the CRM organogram. Rivierwijk is jointly held by
  // three holdings (35 + 25 + 40 = 100%), one of which is Oosterrijck (10020) above.
  { nr: '10035', name: 'Bosstad Bouw Holding B.V.', short: 'Bosstad Bouw', manager: 'MV', form: 'HOLDING' },
  {
    nr: '10038',
    name: 'Westerrijck Projecten Holding B.V.',
    short: 'Westerrijck Proj.',
    manager: 'NK',
    form: 'HOLDING',
  },
  { nr: '10040', name: 'Rivierwijk Techniek B.V.', short: 'Rivierwijk Techniek', manager: 'HM', form: 'BV' },
  {
    nr: '10121',
    name: 'Kanaalstede Logistiek B.V.',
    short: 'Kanaalstede Log.',
    manager: 'MV',
    form: 'BV',
    parent: '10035',
  },
  { nr: '10124', name: 'Duinbrug Beheer B.V.', short: 'Duinbrug Beheer', manager: 'MV', form: 'BV', parent: '10035' },
  { nr: '10131', name: 'Amstelpoort B.V.', short: 'Amstelpoort', manager: 'NK', form: 'BV', parent: '10038' },

  { nr: '10044', name: 'Scheldeland Retail B.V.', short: 'Scheldeland Retail', manager: 'LD', form: 'BV' },
  { nr: '10047', name: 'Maasbrug Consult B.V.', short: 'Maasbrug Consult', manager: 'AS', form: 'BV' },
  { nr: '10052', name: 'Korenwaard B.V.', short: 'Korenwaard', manager: 'LD', form: 'BV' },
  { nr: '10058', name: 'Stichting Wijkhuis Noord', short: 'Stichting Wijkhuis', manager: 'AS', form: 'STICHTING' },

  // Further year-work clients. They sit past the ninth row, so the boards that show only the top of
  // the wall are unaffected; the planning hero runs long enough to need them.
  { nr: '10060', name: 'Meerdijk Techniek B.V.', short: 'Meerdijk Techniek', manager: 'DA', form: 'BV' },
  { nr: '10063', name: 'Van Loon Advies B.V.', short: 'Van Loon Advies', manager: 'DA', form: 'BV' },
  { nr: '10066', name: 'Brinkhof Groep B.V.', short: 'Brinkhof Groep', manager: 'DA', form: 'HOLDING' },
  { nr: '10069', name: 'Stadhaven Logistiek B.V.', short: 'Stadhaven Log.', manager: 'DA', form: 'BV' },
  { nr: '10072', name: 'De Wit Installaties', short: 'De Wit Installaties', manager: 'DA', form: 'EENMANSZAAK' },
  { nr: '10075', name: 'Kruisland Vastgoed B.V.', short: 'Kruisland Vastgoed', manager: 'DA', form: 'BV' },
  { nr: '10078', name: 'Veenstra Bouw B.V.', short: 'Veenstra Bouw', manager: 'DA', form: 'BV' },
  { nr: '10081', name: 'Hofmeijer Consultancy', short: 'Hofmeijer Cons.', manager: 'HM', form: 'EENMANSZAAK' },
  { nr: '10084', name: 'Zandvliet Retail B.V.', short: 'Zandvliet Retail', manager: 'NK', form: 'BV' },
  { nr: '10087', name: 'Ravenstein Beheer B.V.', short: 'Ravenstein Beheer', manager: 'TV', form: 'HOLDING' },
  { nr: '10090', name: 'Duinkerken Transport B.V.', short: 'Duinkerken Tr.', manager: 'SV', form: 'BV' },
  { nr: '10093', name: 'Lelyhof Zorg B.V.', short: 'Lelyhof Zorg', manager: 'YE', form: 'BV' },
  { nr: '10096', name: 'Beekman & Zonen', short: 'Beekman & Zonen', manager: 'AS', form: 'VOF' },

  // Further holdings. A practice this size has them by the dozen, so a search on "holding" has to
  // return a list rather than a handful — these are what the client list narrows to.
  { nr: '10140', name: 'Rijnpoort Beheer Holding B.V.', short: 'Rijnpoort Beheer', manager: 'MV', form: 'HOLDING' },
  { nr: '10143', name: 'Veldhoven Groep Holding B.V.', short: 'Veldhoven Groep', manager: 'NK', form: 'HOLDING' },
  { nr: '10146', name: 'Zuiderhof Holding B.V.', short: 'Zuiderhof', manager: 'TV', form: 'HOLDING' },
  { nr: '10149', name: 'Maasdal Beheer Holding B.V.', short: 'Maasdal Beheer', manager: 'LD', form: 'HOLDING' },
  { nr: '10152', name: 'Eemsland Holding B.V.', short: 'Eemsland', manager: 'AS', form: 'HOLDING' },

  // Near-namesakes. A base this size holds several firms whose names start the same way, which is
  // precisely why a booking picks its client from a lookup instead of typing one out.
  { nr: '10160', name: 'Noorderbosch IT B.V.', short: 'Noorderbosch IT', manager: 'HM', form: 'BV' },
  { nr: '10163', name: 'Noorderdam Techniek B.V.', short: 'Noorderdam Techniek', manager: 'NK', form: 'BV' },
  { nr: '10166', name: 'Noorderkade Beheer B.V.', short: 'Noorderkade Beheer', manager: 'TV', form: 'BV' },
] as const satisfies readonly Client[]

export type ClientNr = (typeof CLIENTS)[number]['nr']

const CLIENTS_BY_NR = new Map(CLIENTS.map((client) => [client.nr as string, client as Client]))

/** Looks a client up by number; throws in development if the number is unknown. */
export function client(nr: ClientNr): Client {
  const found = CLIENTS_BY_NR.get(nr)
  if (!found) throw new Error(`Unknown client: ${nr}`)
  return found
}

/**
 * Total clients in the fictional firm's base. Every volume on the site is a share of this — the
 * year activities, the monthly load, the filter counts — so the firm can be resized from here and
 * nothing on any card falls out of proportion with anything else.
 */
export const CLIENT_COUNT = 812

/** A share of the client base, rounded — how many clients a given obligation applies to. */
export const clientShare = (pct: number) => Math.round((CLIENT_COUNT * pct) / 100)

/**
 * What the client list is searched on, and how much of the base matches it. The list on screen is a
 * window on the base, so the count is a share of the whole rather than of the rows in view.
 */
export const CLIENT_SEARCH = { query: 'hold', results: clientShare(11.8) } as const

/**
 * The window on the client base that the list screens open on — the same clients in the same order
 * everywhere a "klanten" list is shown, on the desktop and on the phone. `team` is who works on
 * that client, the responsible manager first, so a row's avatars agree with `CLIENTS[].manager`.
 */
export const CLIENT_LIST: readonly { nr: ClientNr; team: readonly TeamInitials[] }[] = [
  { nr: '10001', team: ['DA', 'MV'] },
  { nr: '10002', team: ['DA'] },
  { nr: '10003', team: ['DA', 'LD'] },
  { nr: '10010', team: ['HM'] },
  { nr: '10014', team: ['HM', 'TV'] },
  { nr: '10018', team: ['YE'] },
  { nr: '10020', team: ['MV', 'TV', 'NK'] },
  { nr: '10024', team: ['TV'] },
  { nr: '10027', team: ['DA'] },
]

/* ------------------------------------------------- client dossier detail */

/**
 * What a contact does at the client. A key rather than a label, for the same reason the staff roles
 * are: the chip beside "S. Meijer" has to read in the language of the page around it.
 */
export type ContactRoleKey = 'directie' | 'financieleAdministratie' | 'eigenaar'

/**
 * Full dossier detail for the two clients that get an entire visual to themselves. KvK numbers are
 * 8 digits, BTW numbers follow `NL` + 9 digits + `B` + 2, and the IBANs carry a valid bank code —
 * the formats a Dutch accountant would recognise at a glance.
 *
 * They are deliberately not valid, though, only plausible. Every IBAN here fails the IBAN-97
 * checksum, so none of them can ever address a real account however convincingly it reads, and the
 * VAT numbers are all unknown to VIES. Keep it that way when adding a client: change the account
 * number, not the check digits, and re-run the checksum.
 *
 * KvK numbers have no checksum, so there is nothing to deliberately fail and no way to prove a
 * number is unissued from the number alone — one of these turned out to belong to a real company.
 * They are taken from the top of the 8-digit space instead, which the register works towards last,
 * and a new one still has to be looked up on kvk.nl before it goes in. Do NOT "change one digit"
 * to make a number fictional: the register issues them in sequence, so the neighbour of a real
 * company is the most likely number to be real too.
 */
export const CLIENT_DETAILS = {
  '10010': {
    email: 'info@noorderpoortit.nl',
    city: 'Zwolle',
    phone: '+31 38 4267910',
    kvk: '37101234',
    btw: 'NL861204957B01',
    iban: 'NL69 RABO 0345 7496 72',
    addresses: [
      { street: 'Schoolstraat 85', tag: 'ZAKELIJK', place: '8032 AC, Zwolle · Nederland' },
      { street: 'Postbus 214', tag: 'POST', place: '8000 AE, Zwolle · Nederland' },
    ],
    contacts: [
      { name: 'J. van der Heijden', nickname: 'Jasper', role: 'directie' },
      { name: 'S. Meijer', nickname: 'Sven', role: 'financieleAdministratie' },
    ],
  },
  '10002': {
    email: 'administratie@vandijkvastgoed.nl',
    phone: '+31 26 3841170',
    website: 'vandijkvastgoed.nl',
    kvk: '09148802',
    btw: 'NL803472819B01',
    iban: 'NL54 INGB 0912 5536 12',
    city: 'Arnhem',
    addresses: [{ street: 'Velperweg 118', tag: 'ZAKELIJK', place: '6824 HK, Arnhem · Nederland' }],
    contacts: [{ name: 'M. van Dijk', nickname: 'Martijn', role: 'directie' }],
  },
  '10040': {
    email: 'administratie@rivierwijktechniek.nl',
    phone: '+31 570 613482',
    kvk: '99820147',
    btw: 'NL814962531B01',
    iban: 'NL91 INGB 0004 8829 33',
    city: 'Deventer',
    addresses: [{ street: 'Handelskade 42', tag: 'ZAKELIJK', place: '7417 DE, Deventer · Nederland' }],
    contacts: [{ name: 'R. Bosman', nickname: 'Rick', role: 'eigenaar' }],
  },
} as const

/**
 * The rest of Noorderpoort IT's dossier: how to reach its contacts, the documents filed against it
 * and what the firm has done on it lately. `ClientRecordVisual` shows the record's fields; this is
 * the same dossier's other half, and the two must not describe different people — the names here
 * are the ones in `CLIENT_DETAILS['10010'].contacts`, in the same order.
 */
export const CONTACT_LINES = [
  { email: 'j.vanderheijden@noorderpoortit.nl', phone: '+31 6 24118307' },
  { email: 's.meijer@noorderpoortit.nl', phone: '+31 38 4267914' },
] as const

/** `sizeKb` rather than a formatted string, so each language writes the number its own way. */
export type ClientFile = { name: string; kind: 'pdf' | 'xlsx' | 'docx'; day: number; sizeKb: number }

/**
 * Documents on the dossier, newest first. Named the way a firm names them: the year in the file
 * name, because that is what someone searching the dossier a year later will type.
 */
export const CLIENT_FILES: readonly ClientFile[] = [
  { name: 'Kolommenbalans 2025.xlsx', kind: 'xlsx', day: 15, sizeKb: 196 },
  { name: 'Jaarrekening 2025.pdf', kind: 'pdf', day: 14, sizeKb: 842 },
  { name: 'Opdrachtbevestiging.pdf', kind: 'pdf', day: 3, sizeKb: 118 },
] as const

/**
 * The dossier's log, newest first. `key` names the sentence the copy tables write out; the member
 * and the day stay here, with the rest of the universe.
 */
export type ClientLogKey = 'filed' | 'contactAdded' | 'fileUploaded' | 'taskCompleted' | 'vatFiled'
/**
 * `file` names the document on this dossier the entry is about, where there is one. It is what
 * lets a card draw the line between the two — a log entry and the document it concerns are one
 * event filed in two places, and nothing else in the data says which document that is.
 */
export type ClientLogEntry = { key: ClientLogKey; member: TeamInitials; day: number; file?: string }

/**
 * The dossier's log, newest first.
 *
 * Deliberately not only documents and contacts. A firm's log is where the rest of the system leaves
 * its trace: a task ticked off, hours booked, a return filed. Those are the tabs of the dossier
 * hero at the top of this page, and a log that records them is the same claim as that strip, made
 * from the other side — which is why this card can drop the contacts it was duplicating and still
 * be about a client file with everything in one place.
 */
export const CLIENT_LOG: readonly ClientLogEntry[] = [
  // Newest, and the one the card replays: an upload is the one kind of entry that puts something on
  // the shelf as well as a line in the log, which is what makes it worth watching happen.
  { key: 'fileUploaded', member: 'AS', day: 15, file: 'Kolommenbalans 2025.xlsx' },
  { key: 'filed', member: 'SV', day: 15, file: 'Jaarrekening 2025.pdf' },
  { key: 'taskCompleted', member: 'NK', day: 14 },
  { key: 'vatFiled', member: 'HM', day: 11 },
  { key: 'contactAdded', member: 'SV', day: 3 },
]

/** Rivierwijk's shareholders, as drawn in the organogram and the CRM hero. Sums to 100%. */
export const RIVIERWIJK_SHAREHOLDERS = [
  { nr: '10035', pct: 35 },
  { nr: '10020', pct: 25 },
  { nr: '10038', pct: 40 },
] as const

/** Entities in the Rivierwijk structure once the organogram is fully expanded. */
export const RIVIERWIJK_ENTITY_COUNT = 12

/* -------------------------------------------------------- planning model */

/**
 * State of one planning cell.
 * - `gepland` — scheduled, not started
 * - `loopt` — in progress
 * - `controle` — waiting for review by an accountant
 * - `klaar` — done
 * - `leeg` — not applicable to this client (rendered as a dash)
 */
export type PlanningState = 'klaar' | 'loopt' | 'controle' | 'gepland' | 'leeg'

/** A cell that flips from one state to another partway through a visual's loop. */
export type PlanningCell = PlanningState | { from: PlanningState; to: PlanningState; at: number }

/** Every status any board can show — the year board's set plus the month board's `ontvangen`. */
export type StatusKey = PlanningState | 'ontvangen'

export type StatusSpec = {
  /** Human label, as it reads on a board cell or a badge. */
  label: string
  /**
   * Fill colour, as a CSS custom property that every app visual defines. `null` means the status
   * has no fill: `dashed` draws a dashed outline, `empty` renders as a bare dash.
   */
  color: string | null
  shape: 'filled' | 'dashed' | 'empty'
}

/**
 * The single status vocabulary for the whole site: label, colour and shape per status. Board cells,
 * badges and pop-ups all read this, so a "Loopt" is the same word in the same blue everywhere.
 */
export const PLANNING_STATUS: Record<StatusKey, StatusSpec> = {
  klaar: { label: 'Klaar ✓', color: 'var(--au-cell-done)', shape: 'filled' },
  loopt: { label: 'Loopt', color: 'var(--au-busy)', shape: 'filled' },
  controle: { label: 'Controle', color: 'var(--au-ctrl)', shape: 'filled' },
  ontvangen: { label: 'Ontvangen', color: 'var(--au-recv)', shape: 'filled' },
  gepland: { label: 'Gepland', color: null, shape: 'dashed' },
  leeg: { label: '—', color: null, shape: 'empty' },
}

/**
 * The lifecycle a single werkzaamheid moves through inside its own detail screen — a different axis
 * from `PLANNING_STATUS`, which describes a cell on a board. Ordered: this is the sequence the
 * status list renders, and the order the scenarios step through. Each step carries who signed it
 * off and when, so the audit trail follows the work from bookkeeper to reviewer to accountant.
 */
export const WORKFLOW_STATUS = [
  { key: 'ontvangen', label: 'Administratie ontvangen', color: 'var(--au-recv)', by: 'YE', day: 19, at: '10:15' },
  { key: 'uitvoering', label: 'In uitvoering', color: 'var(--au-busy)', by: 'HM', day: 21, at: '14:51' },
  { key: 'controle', label: 'Gereed voor controle', color: 'var(--au-ctrl)', by: 'TV', day: 21, at: '16:57' },
  { key: 'afgerond', label: 'Afgerond', color: 'var(--au-cell-done)', by: 'AS', day: 22, at: '16:48' },
] as const

export type WorkflowStatusKey = (typeof WORKFLOW_STATUS)[number]['key']

function workflowStatus(key: WorkflowStatusKey) {
  const found = WORKFLOW_STATUS.find((status) => status.key === key)
  if (!found) throw new Error(`Unknown workflow status: ${key}`)
  return found
}

/**
 * The period the visuals are set in, as a bare year. The firm is working in July 2026: the month
 * boards show that month, capacity and fee budgets are planned for the running book year, and the
 * year board is closing the previous one — which is exactly how an accountancy practice's July
 * looks. The spelled-out labels ("Boekjaar 2026" / "Financial year 2026") live in the copy tables,
 * because they are read on screen; this is the number the places that label a column or a field
 * with the year alone put there. It is written out rather than sliced off one of those labels: the
 * two languages do not carry the year in the same word, and slicing gave the English page a column
 * headed "year".
 */
export const CURRENT_YEAR = '2026'

/* --------------------------------------------------------- year planning */

/**
 * The recurring yearly compliance activities, in the order they are worked: the bookkeeping is
 * closed, the annual accounts are prepared and adopted, the corporate tax return follows from them,
 * and the accounts are deposited at the KvK. Average hours reflect what these activities actually
 * take — an annual account is many times the work of a KvK deposit.
 */
export const YEAR_ACTIVITIES = [
  { key: 'jaarrekening', label: 'Jaarrekening', short: 'JR', avgHours: 8.9, items: clientShare(72.6), donePct: 57 },
  {
    key: 'vpb',
    label: 'VPB',
    short: 'VPB',
    fullLabel: 'Vennootschapsbelasting (VPB)',
    avgHours: 3.0,
    items: clientShare(40.7),
    donePct: 44,
  },
  {
    key: 'kvk',
    label: 'KvK',
    short: 'KvK',
    fullLabel: 'KvK-publicatie',
    avgHours: 1.6,
    items: clientShare(46.9),
    donePct: 61,
  },
  {
    key: 'ib',
    label: 'IB',
    short: 'IB',
    fullLabel: 'Inkomstenbelasting (IB)',
    avgHours: 1.6,
    items: clientShare(54.1),
    donePct: 38,
  },
] as const

/**
 * Yearly work that is NOT a column on the board but still counts towards the firm's year total —
 * closing the books and the odd VAT correction. Splitting these out is what lets the board's
 * headline ("x van y taken") be derived rather than invented, so it can never contradict the
 * per-column percentages again.
 */
const YEAR_OFF_BOARD_ACTIVITIES = [
  {
    key: 'jaarafsluiting',
    label: 'Jaarafsluiting administratie',
    avgHours: 3.1,
    items: clientShare(92.8),
    donePct: 92,
  },
  { key: 'suppletie', label: 'Suppletie omzetbelasting', avgHours: 1.5, items: clientShare(24.5), donePct: 85 },
] as const

const ALL_YEAR_ACTIVITIES = [...YEAR_ACTIVITIES, ...YEAR_OFF_BOARD_ACTIVITIES]

/** Completed items for one year activity, from its share and its size. */
export function yearActivityDone(activity: { items: number; donePct: number }): number {
  return Math.round((activity.items * activity.donePct) / 100)
}

export type YearActivityKey = (typeof YEAR_ACTIVITIES)[number]['key']

export type YearPlanningRow = {
  nr: ClientNr
  /** One cell per entry in `YEAR_ACTIVITIES`, in that order. */
  cells: readonly [PlanningCell, PlanningCell, PlanningCell, PlanningCell]
  /** Responsible colleague for this client's year work. */
  owner: TeamInitials
  /** Month the year work is due — the board's own `Maand` column, and what a month filter matches. */
  deadline: string
}

/**
 * The year-planning board (boekjaar 2025).
 *
 * Every row obeys the domain rules at the top of this file. Three cells flip to `klaar` partway
 * through the hero loop (beats 11, 15 and 17) and two cells start up (beats 2 and 5), which is what
 * makes the board feel alive; the flip beats are referenced by `JaarplanningVisual`.
 */
export const YEAR_PLANNING = [
  // Holding: accounts adopted and deposited, corporate tax still being prepared.
  { nr: '10001', owner: 'DA', deadline: 'Augustus', cells: ['klaar', 'loopt', 'klaar', 'leeg'] },
  // The hero row: Anouk finishes the annual accounts mid-loop, so VPB and KvK are still scheduled.
  {
    nr: '10002',
    owner: 'AS',
    deadline: 'September',
    cells: [{ from: 'loopt', to: 'klaar', at: 11 }, 'gepland', 'gepland', 'leeg'],
  },
  // Accounts done, so the KvK deposit can start. Same administrator as its holding above: year work
  // is assigned per group, which is also why filtering on a colleague returns a cluster of clients.
  {
    nr: '10003',
    owner: 'DA',
    deadline: 'November',
    cells: ['klaar', 'gepland', { from: 'gepland', to: 'loopt', at: 5 }, 'leeg'],
  },
  // Eenmanszaak: income tax only — no corporate tax, no KvK deposit.
  { nr: '10018', owner: 'YE', deadline: 'Oktober', cells: ['leeg', 'leeg', 'leeg', 'klaar'] },
  {
    nr: '10014',
    owner: 'HM',
    deadline: 'September',
    cells: [{ from: 'loopt', to: 'klaar', at: 15 }, 'gepland', 'gepland', 'leeg'],
  },
  // Eenmanszaak: accounts are compiled for the income tax return, which is now running.
  {
    nr: '10021',
    owner: 'NK',
    deadline: 'November',
    cells: ['gepland', 'leeg', 'leeg', { from: 'gepland', to: 'loopt', at: 2 }],
  },
  {
    nr: '10024',
    owner: 'TV',
    deadline: 'Oktober',
    cells: [{ from: 'controle', to: 'klaar', at: 17 }, 'loopt', 'gepland', 'leeg'],
  },
  // VOF: the partners file income tax; a VOF does not publish at the KvK.
  { nr: '10027', owner: 'DA', deadline: 'Augustus', cells: ['klaar', 'leeg', 'leeg', 'loopt'] },
  // Belgian SRL: files Belgian corporate tax and deposits with the NBB, not the Dutch KvK.
  { nr: '10031', owner: 'SV', deadline: 'December', cells: ['loopt', 'gepland', 'leeg', 'leeg'] },

  // The rest of the wall. Only the planning-page hero runs long enough to show these, and it is the
  // one that filters — so this tail is what makes a colleague's own clients a full board rather
  // than three rows. Their annual accounts are all done, which keeps `UNFINISHED_YEARWORK` (and the
  // assistant's answer built on it) to the same five clients the top of the wall already had.
  { nr: '10060', owner: 'DA', deadline: 'Augustus', cells: ['klaar', 'loopt', 'klaar', 'leeg'] },
  { nr: '10063', owner: 'DA', deadline: 'Augustus', cells: ['klaar', 'klaar', 'loopt', 'leeg'] },
  { nr: '10066', owner: 'DA', deadline: 'Augustus', cells: ['klaar', 'gepland', 'gepland', 'leeg'] },
  { nr: '10069', owner: 'DA', deadline: 'September', cells: ['klaar', 'loopt', 'gepland', 'leeg'] },
  { nr: '10072', owner: 'DA', deadline: 'Oktober', cells: ['klaar', 'leeg', 'leeg', 'loopt'] },
  { nr: '10075', owner: 'DA', deadline: 'September', cells: ['klaar', 'gepland', 'klaar', 'leeg'] },
  { nr: '10078', owner: 'DA', deadline: 'Oktober', cells: ['klaar', 'klaar', 'klaar', 'leeg'] },
  { nr: '10081', owner: 'HM', deadline: 'Augustus', cells: ['klaar', 'leeg', 'leeg', 'gepland'] },
  { nr: '10084', owner: 'NK', deadline: 'September', cells: ['klaar', 'loopt', 'gepland', 'leeg'] },
  { nr: '10087', owner: 'TV', deadline: 'Oktober', cells: ['klaar', 'klaar', 'loopt', 'leeg'] },
  { nr: '10090', owner: 'SV', deadline: 'November', cells: ['klaar', 'gepland', 'gepland', 'leeg'] },
  { nr: '10093', owner: 'YE', deadline: 'Oktober', cells: ['klaar', 'gepland', 'gepland', 'leeg'] },
  { nr: '10096', owner: 'AS', deadline: 'December', cells: ['klaar', 'leeg', 'leeg', 'klaar'] },
] as const satisfies readonly YearPlanningRow[]

/**
 * The top of the wall — the nine rows every board except the planning hero shows. Keeping it a
 * named slice means the wall can grow a tail without moving any of those boards.
 */
export const YEAR_BOARD = YEAR_PLANNING.slice(0, 9)

/**
 * What the board reports as its filters narrow it. The row list on screen is a sample of the firm,
 * so these carry the firm-scale figures the header shows — the same three numbers the real board
 * recomputes when a filter lands: how many clients are left, and how far along they are.
 */
export const YEAR_WALL_FILTERS = {
  none: { clients: CLIENT_COUNT, donePct: 65 },
  /** Filtered to one colleague's own clients. */
  owner: { initials: 'DA' as TeamInitials, clients: clientShare(12.5), donePct: 62 },
  /** ...and narrowed again to one deadline month. */
  month: { label: 'Augustus', clients: clientShare(3.7), donePct: 71 },
  /** What the panel offers to filter on: colleagues, deadline months, and the client tags. */
  options: {
    people: ['MV', 'AS', 'DA', 'NK'] as readonly TeamInitials[],
    months: ['Juli', 'Augustus', 'September', 'Oktober'],
    tags: ['Holding', 'BV', 'Eenmanszaak', 'VOF'],
  },
} as const

/** Beats at which a year-planning cell flips to `klaar` — each one ticks the done counter. */
export const YEAR_FLIP_BEATS = [11, 15, 17] as const

/**
 * Firm-wide year totals behind the board header — derived from the activities above, so the
 * headline count and the column percentages are two views of one dataset.
 */
export const YEAR_TOTALS = {
  /** Completed at the start of the loop; ticks up by one per flip. */
  doneAtStart: ALL_YEAR_ACTIVITIES.reduce((sum, activity) => sum + yearActivityDone(activity), 0),
  total: ALL_YEAR_ACTIVITIES.reduce((sum, activity) => sum + activity.items, 0),
} as const

/** Completion per year activity, firm-wide — read straight off the activity list. */
export const YEAR_ACTIVITY_PCT = Object.fromEntries(
  YEAR_ACTIVITIES.map((activity) => [activity.key, activity.donePct]),
) as Record<YearActivityKey, number>

/* -------------------------------------------------------- month planning */

/**
 * The recurring monthly activities. Bookkeeping is processed first, the VAT return follows from it,
 * and payroll runs alongside with its own wage-tax return.
 */
export const MONTH_ACTIVITIES = [
  { key: 'administratie', label: 'Administratie', fullLabel: 'Financiële administratie', avgHours: 1.2 },
  { key: 'omzetbelasting', label: 'Omzetbelasting', fullLabel: 'Omzetbelastingaangifte', avgHours: 0.9 },
  { key: 'salaris', label: 'Salaris', fullLabel: 'Salarisverwerking', avgHours: 2.0 },
  { key: 'loonheffingen', label: 'Loonheffingen', fullLabel: 'Aangifte loonheffingen', avgHours: 0.6 },
] as const

export type MonthActivityKey = (typeof MONTH_ACTIVITIES)[number]['key']

/**
 * Monthly work runs through its own short cycle, which is what the board colours encode:
 * `gepland` (scheduled, nobody on it yet) -> `ontvangen` (the client delivered their paperwork) ->
 * `loopt` -> `klaar`. `leeg` marks an activity the client does not have at all, such as payroll for
 * a company without staff.
 */
export type MonthState = 'ontvangen' | 'loopt' | 'klaar' | 'gepland' | 'leeg'

export type MonthPlanningRow = {
  nr: ClientNr
  /** One cell per entry in `MONTH_ACTIVITIES`, in that order. */
  cells: readonly [MonthState, MonthState, MonthState, MonthState]
  /** Colleague responsible per activity; `null` where the activity does not apply. */
  assignees: readonly [TeamInitials | null, TeamInitials | null, TeamInitials | null, TeamInitials | null]
  /** Date the client's paperwork came in (dd-mm), shown on the `ontvangen` administratie cell. */
  receivedOn?: string
}

/**
 * The month-planning board (juli 2026).
 *
 * Deliberately a DIFFERENT slice of the client base than `YEAR_PLANNING` — the two boards share no
 * clients — so the year board and the month board read as two genuinely different views of the firm
 * rather than the same eight names twice. Every visual that shows monthly work uses this list.
 *
 * A VAT return can only be filed once that month's bookkeeping is processed, so a running or done
 * `omzetbelasting` cell never sits next to an unstarted `administratie` cell. Payroll runs on its
 * own cycle, so it is not bound to the bookkeeping; clients without staff have no payroll at all.
 */
export const MONTH_PLANNING: readonly MonthPlanningRow[] = [
  // --- Bosstad group: a holding with two operating subsidiaries.
  // Holding without staff: paperwork just came in, VAT waits on the bookkeeping, no payroll.
  {
    nr: '10035',
    cells: ['ontvangen', 'gepland', 'leeg', 'leeg'],
    assignees: ['DA', null, null, null],
    receivedOn: '15-07',
  },
  { nr: '10121', cells: ['klaar', 'loopt', 'klaar', 'klaar'], assignees: ['LD', 'MV', 'YE', 'YE'] },
  // A pure holding-like BV: books closed and VAT filed, but nobody on the payroll.
  { nr: '10124', cells: ['klaar', 'klaar', 'leeg', 'leeg'], assignees: ['LD', 'LD', null, null] },

  // The client this month is followed through, left to right: books closed, VAT filed, payroll
  // running, and its wage-tax return waiting on that payroll run.
  { nr: '10010', cells: ['klaar', 'klaar', 'loopt', 'gepland'], assignees: ['HM', 'NK', 'YE', 'YE'] },

  // --- Westerrijck group: a second structure further down the board, so the tree is not a
  // one-off at the top.
  {
    nr: '10038',
    cells: ['ontvangen', 'gepland', 'leeg', 'leeg'],
    assignees: ['YE', null, null, null],
    receivedOn: '18-07',
  },
  // Bookkeeping still running, so its VAT return cannot start yet.
  { nr: '10131', cells: ['loopt', 'gepland', 'klaar', 'klaar'], assignees: ['DA', null, 'YE', 'YE'] },

  // A foundation is VAT-exempt — no omzetbelasting at all — but does run payroll.
  { nr: '10058', cells: ['loopt', 'leeg', 'ontvangen', 'gepland'], assignees: ['AS', null, 'YE', 'YE'] },
]

/**
 * How the spreadsheet the firm is leaving behind spells each client, and what its cells actually
 * hold. Deliberately cramped and inconsistent — abbreviations, stray dashes, a "zie mail" where a
 * status belongs — because that is the point of the before-state. It lives here rather than in the
 * visual so the sheet and the board it becomes are two views of one client list.
 */
export const EXCEL_SHEET: Partial<Record<ClientNr, { name: string; tag: string; cells: readonly string[] }>> = {
  '10035': { name: 'Bosstad Bouw', tag: 'HOLD', cells: ['zie mail', '###', 'n.v.t.'] },
  '10121': { name: '- Kanaalstede', tag: 'BV', cells: ['ok', 'wie??', ''] },
  '10124': { name: '- Duinbrug', tag: 'BV', cells: ['ok', '', 'n.v.t.'] },
  '10010': { name: 'Noorderpoort', tag: 'BV', cells: ['ok', '12-7??', '###'] },
  '10038': { name: 'Westerrijck', tag: 'HOLD', cells: ['', '###', '??'] },
  '10131': { name: '- Amstelpoort', tag: 'BV', cells: ['deels?', '', 'ok'] },
  '10058': { name: 'Stichting Wijkh.', tag: 'STG', cells: ['tabblad 3?', 'vrijgest.', '###'] },
}

/** Which of those cells read as a problem rather than as a value. */
export const EXCEL_WARN: ReadonlySet<string> = new Set(['###', '??', 'wie??', '12-7??', 'deels?', 'tabblad 3?'])

/** ...and which are a legitimate "does not apply". */
export const EXCEL_MUTED: ReadonlySet<string> = new Set(['n.v.t.', 'vrijgest.'])

/**
 * The colleague responsible for one activity on a client's month-planning row.
 *
 * Throws when the row or the assignment does not exist, so a visual that names a handler can never
 * pick one of its own: whoever a dialog shows is whoever the board shows for that same cell.
 */
export function monthAssignee(nr: ClientNr, activity: MonthActivityKey): TeamMember {
  const index = MONTH_ACTIVITIES.findIndex((item) => item.key === activity)
  const initials = MONTH_PLANNING.find((row) => row.nr === nr)?.assignees[index]
  if (!initials) throw new Error(`No ${activity} assignee on the month planning for client ${nr}`)
  return teamMember(initials)
}

export type MonthLoad = {
  month: string
  /** Activities planned that month, across every client. */
  total: number
  /** Of those, how many are finished. */
  done: number
}

/**
 * A month's load as a factor of the client base, and how far it is. Written this way rather than as
 * absolute figures so the whole year rescales with `CLIENT_COUNT`: the quarter months sit around
 * 2.4 obligations per client because the quarterly VAT filers come on top of the monthly ones, the
 * rest around 1.2, and August dips for the holidays.
 */

/**
 * The compliance load per month of the book year being worked on, the figures behind the year
 * chart. Labelled `closingBookYear`: the work runs through the calendar year after the one it
 * belongs to, which is why a board in July 2026 is a 2025 board.
 *
 * The quarter months carry roughly double the rest: the monthly VAT filers file every month, and in
 * January, April, July and October the quarterly filers come on top — which is why a real year
 * chart has four peaks rather than twelve equal columns. August dips for the holidays.
 *
 * June sits just short of complete: a handful of returns are still waiting on client paperwork.
 * That is the case worth showing — a closed month that is not actually finished is exactly what a
 * self-updating chart is for.
 */
export const YEAR_MONTH_LOAD: readonly MonthLoad[] = [
  { month: 'jan', load: 2.42, donePct: 100 },
  { month: 'feb', load: 1.2, donePct: 100 },
  { month: 'mrt', load: 1.22, donePct: 100 },
  // A handful of returns still waiting on client paperwork.
  { month: 'apr', load: 2.44, donePct: 98 },
  { month: 'mei', load: 1.19, donePct: 100 },
  // The clearest case of a closed month that is not actually finished.
  { month: 'jun', load: 1.24, donePct: 92 },
  // The running month. Its share is `MONTH_TOTALS.donePct`, so the chart and the month board
  // cannot report different progress for the same July.
  { month: 'jul', load: 2.45, donePct: 52 },
  { month: 'aug', load: 1.16, donePct: 0 },
  { month: 'sep', load: 1.23, donePct: 0 },
  { month: 'okt', load: 2.44, donePct: 0 },
  { month: 'nov', load: 1.21, donePct: 0 },
  { month: 'dec', load: 1.2, donePct: 0 },
].map((row) => {
  const total = Math.round(CLIENT_COUNT * row.load)
  return { month: row.month, total, done: Math.round((total * row.donePct) / 100) }
})

/** Index of the month the firm is working in — July, as everywhere else in these visuals. */
export const CURRENT_MONTH_INDEX = 6

/** Round ceiling just above the busiest month, so a chart's axis reads in halves of it. */
export const MONTH_LOAD_CEILING = Math.ceil(Math.max(...YEAR_MONTH_LOAD.map((row) => row.total)) / 200) * 200

export type MonthTone = 'done' | 'attention' | 'current' | 'future'

/**
 * How a month reads on a progress chart: green once everything in it is finished, orange while a
 * closed month still has work open, blue for the month being worked in, and a bare track for the
 * months not yet reached. Shared so the full chart and the dashboard tile cannot colour the same
 * month differently.
 */
export function monthTone(index: number, row: MonthLoad): MonthTone {
  if (index > CURRENT_MONTH_INDEX) return 'future'
  if (index === CURRENT_MONTH_INDEX) return 'current'
  return row.done >= row.total ? 'done' : 'attention'
}

export const MONTH_TONE_COLOR: Record<MonthTone, string> = {
  done: 'var(--au-done)',
  attention: 'var(--au-sig)',
  current: 'var(--au-accent)',
  future: 'var(--au-text2)',
}

/**
 * Work that is billed but is not a recurring obligation — it happens when a file needs it. Named
 * here because two visuals book hours on it.
 */
const REVIEW_ACTIVITY = 'Kwaliteitsreview dossier'

/** The key an integration authenticates with, masked as the app shows it. */
export const API_KEY = 'sk_live_••••••••••••4f2a'

/** Firm-wide monthly totals behind the month board header. The month it covers is read on screen,
 *  so it is named in the copy tables (`period.month`) rather than here. */
export const MONTH_TOTALS = {
  donePct: 52,
} as const

/**
 * Completion per monthly activity, firm-wide. Bookkeeping runs ahead because the VAT return depends
 * on it, and payroll trails because it is only filed near the end of the month.
 */
export const MONTH_ACTIVITY_PCT: Record<MonthActivityKey, number> = {
  administratie: 68,
  omzetbelasting: 51,
  salaris: 36,
  loonheffingen: 29,
}

/**
 * The first three columns. The board has four, but two of the cards on this site are too narrow to
 * carry the fourth without squeezing the client names out — the same crop the real board takes on a
 * small screen. The card that follows one client's whole month renders all four.
 */
export const MONTH_ACTIVITIES_COMPACT = MONTH_ACTIVITIES.slice(0, 3)

/**
 * How far a client is through its own month: finished activities over the ones that apply to it.
 * The board shows this beside the client name, so a client without payroll is not marked down for
 * work it never had.
 */
export function monthClientPct(row: MonthPlanningRow): number {
  const applicable = row.cells.filter((cell) => cell !== 'leeg')
  if (applicable.length === 0) return 0
  return Math.round((applicable.filter((cell) => cell === 'klaar').length / applicable.length) * 100)
}

/* ------------------------------------------------------------ hours data */

/**
 * The hours booking that recurs across the hours visuals: Anouk books her time on the Van Dijk
 * Vastgoed annual accounts she finishes on the year board. 5,50 hours is a realistic slice of the
 * ~8,9 hours an annual account takes end to end.
 */
export const HOURS_BOOKING = {
  member: 'AS',
  clientNr: '10002',
  activity: 'Jaarrekening',
  hours: '5,50',
  /** When it was booked — the timestamp beside the colleague who booked it. */
  at: '14:24',
} as const

/** The employee whose year of hours is charted in `EmployeeHoursVisual`. */
export const HOURS_EMPLOYEE = {
  member: 'TV',
  /** 40-hour contract, five equal days. */
  contractDays: ['Ma 8', 'Di 8', 'Wo 8', 'Do 8', 'Vr 8'],
  totalHours: '1.102,75',
  sickHours: '32,00',
  leaveBalance: '24,50',
  overtime: '−33,25',
  /**
   * That colleague's year so far, one entry per week: [worked, leave, sick] as a percentage of a
   * full column. The dips are a story — two holiday weeks in the spring, a week off sick — and the
   * totals above are their sum, so the bars and the counters cannot drift apart.
   */
  weeks: [
    [30, 14, 0],
    [62, 0, 8],
    [78, 0, 0],
    [74, 0, 0],
    [74, 0, 0],
    [74, 0, 0],
    [60, 14, 0],
    [66, 0, 12],
    [82, 0, 0],
    [64, 8, 0],
    [58, 0, 16],
    [74, 0, 0],
    [60, 14, 0],
    [11, 60, 0],
    [70, 0, 0],
    [74, 0, 0],
    [15, 58, 0],
    [74, 0, 0],
  ],
} as const

/* ------------------------------------------------------- calendar labels */

/**
 * The working week the hours visuals render, in the month the site is set in. July 2026 starts on a
 * Wednesday, so the Mondays are the 6th, 13th and 20th — writing the grid out here stops each
 * visual from inventing a calendar that does not line up with its own weekday captions.
 */
export type HourEntry = {
  nr: ClientNr
  hours: string
  /**
   * The activity booked, what was written on it, and the time it was booked — only the day a panel
   * opens carries these, because it is the only screen that shows a booking in full.
   */
  activity?: string
  note?: string
  at?: string
}

export type HourDay = {
  /** Day of the month; days outside it (the tail of June) carry `outside`. */
  day: number
  outside?: boolean
  entries: readonly HourEntry[]
}

export type HourWeek = { week: number; days: readonly HourDay[] }

/**
 * The month of hours behind the time-tracking calendar: weeks as rows, Monday through Sunday as
 * columns, each day holding the bookings made on it. July 2026 opens on a Wednesday, so week 27
 * starts in June — which is why the first two cells sit outside the month.
 *
 * Totals are never written down: the day adds up its bookings, the week adds up its days and the
 * month adds up its weeks, so a calendar and its header cannot report different hours.
 */
export const HOURS_MONTH: readonly HourWeek[] = [
  {
    week: 27,
    days: [
      {
        day: 29,
        outside: true,
        entries: [
          { nr: '10035', hours: '4:15' },
          { nr: '10010', hours: '3:45' },
        ],
      },
      {
        day: 30,
        outside: true,
        entries: [
          { nr: '10121', hours: '6:00' },
          { nr: '10031', hours: '2:00' },
        ],
      },
      {
        day: 1,
        entries: [
          { nr: '10044', hours: '6:45' },
          { nr: '10010', hours: '1:15' },
        ],
      },
      {
        day: 2,
        entries: [
          { nr: '10020', hours: '5:30' },
          { nr: '10047', hours: '2:30' },
        ],
      },
      {
        day: 3,
        entries: [
          { nr: '10044', hours: '2:00' },
          { nr: '10052', hours: '6:00' },
        ],
      },
      { day: 4, entries: [] },
      { day: 5, entries: [] },
    ],
  },
  {
    week: 28,
    days: [
      {
        day: 6,
        entries: [
          { nr: '10001', hours: '2:15' },
          { nr: '10052', hours: '5:45' },
        ],
      },
      {
        day: 7,
        entries: [
          { nr: '10038', hours: '1:30' },
          { nr: '10121', hours: '5:30' },
        ],
      },
      // The one day that runs past the contracted eight hours, which is what the month view marks
      // in the signal colour — a day worth looking at, rather than a day that was merely booked.
      {
        day: 8,
        entries: [
          { nr: '10024', hours: '4:45' },
          { nr: '10131', hours: '4:30' },
        ],
      },
      { day: 9, entries: [{ nr: '10014', hours: '8:00' }] },
      { day: 10, entries: [{ nr: '10027', hours: '8:00' }] },
      { day: 11, entries: [] },
      { day: 12, entries: [] },
    ],
  },
  {
    week: 29,
    days: [
      { day: 13, entries: [{ nr: '10003', hours: '8:00' }] },
      {
        day: 14,
        entries: [
          { nr: '10058', hours: '3:30' },
          { nr: '10018', hours: '4:30' },
        ],
      },
      // The day the hero books on — the same 1:30 the planning card's checklist produces. It is
      // also the day `HoursDayVisual` opens, which is why its entries carry an activity and a time.
      {
        day: 15,
        entries: [
          {
            nr: '10124',
            hours: '6:30',
            activity: YEAR_ACTIVITIES[0].label,
            note: 'concept',
            at: '09:12',
          },
          {
            nr: '10010',
            hours: '1:30',
            activity: MONTH_ACTIVITIES[0].fullLabel,
            note: 'entry',
            at: '15:42',
          },
        ],
      },
      { day: 16, entries: [{ nr: '10021', hours: '8:00' }] },
      {
        day: 17,
        entries: [
          { nr: '10040', hours: '5:15' },
          { nr: '10047', hours: '2:45' },
        ],
      },
      { day: 18, entries: [] },
      { day: 19, entries: [] },
    ],
  },
  {
    week: 30,
    days: [
      { day: 20, entries: [{ nr: '10002', hours: '8:00' }] },
      // Booked past the contracted eight hours, and one of the days that lands during the loop, so
      // the mark for a long day appears on a cell the eye is being sent to anyway.
      {
        day: 21,
        entries: [
          { nr: '10066', hours: '4:00' },
          { nr: '10063', hours: '4:45' },
        ],
      },
      {
        day: 22,
        entries: [
          { nr: '10060', hours: '2:30' },
          { nr: '10078', hours: '4:00' },
        ],
      },
      { day: 23, entries: [] },
      { day: 24, entries: [] },
      { day: 25, entries: [] },
      { day: 26, entries: [] },
    ],
  },
]

/** Contracted hours per weekday — the target a day header shows next to its date. */
export const HOURS_DAY_TARGET = '08:00'

/**
 * The booking the week grid lands: a full working day on the annual accounts, on the one day of the
 * week that is still empty. One record, so the day the grid highlights, the client the dialog picks
 * and the hours the header ticks up by can never drift apart.
 */
export const HOURS_WEEK_BOOKING = {
  day: 15,
  nr: '10010' as ClientNr,
  activity: YEAR_ACTIVITIES[0].label,
  hours: HOURS_DAY_TARGET,
} as const

/**
 * The day panel: what one day of the calendar above holds when you open it, and the booking that is
 * made in it. The day and its entries are not authored here — they are read out of `HOURS_MONTH`,
 * so the panel and the month behind it can only ever show the same day.
 *
 * The booking is the last entry of that day: the panel shows the day as it was, and then makes the
 * entry that completes it.
 */
export const HOURS_DAY_PANEL = {
  day: 15,
  /** What is typed into the client lookup — enough letters to narrow the base, not to pick. */
  query: 'noor',
  project: CURRENT_YEAR,
} as const

/** The entries booked on a given day, in the order they were made. */
export function hoursOfDay(day: number): readonly HourEntry[] {
  const found = HOURS_MONTH.flatMap((week) => week.days).find((row) => row.day === day && !row.outside)
  if (!found) throw new Error(`No day ${day} in the hours month`)
  return found.entries
}

/** Every client the lookup turns up for a query — the reason you pick one instead of typing it. */
export function clientMatches(query: string): readonly Client[] {
  return (CLIENTS as readonly Client[]).filter((row) => row.name.toLowerCase().includes(query.toLowerCase()))
}

const hourMinutes = (hours: string) => {
  const [h, m] = hours.split(':')
  return Number(h) * 60 + Number(m)
}

export const formatHours = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`

/** Hours booked on a day, summed from its entries. */
export function dayMinutes(day: HourDay, skip?: HourEntry): number {
  return day.entries.reduce((sum, entry) => sum + (entry === skip ? 0 : hourMinutes(entry.hours)), 0)
}

/** Hours booked in a week, summed from its days. */
export function weekMinutes(week: HourWeek, skip?: HourEntry): number {
  return week.days.reduce((sum, day) => sum + dayMinutes(day, skip), 0)
}

/**
 * What the phone shows: the last few days this colleague booked, and the entry the animation books
 * on top of them. Days are day-of-month in the period above, so their weekday headers are derived
 * rather than typed — the phone can never claim a Thursday the calendar calls a Friday.
 */
export const PHONE_HOURS = {
  days: [
    {
      day: 9,
      entries: [
        { nr: '10010', activity: YEAR_ACTIVITIES[0].label, hours: '4:30' },
        { nr: '10014', activity: MONTH_ACTIVITIES[0].fullLabel, hours: '3:30' },
      ],
    },
    {
      day: 10,
      entries: [
        { nr: '10044', activity: MONTH_ACTIVITIES[1].fullLabel, hours: '5:15' },
        { nr: '10044', activity: REVIEW_ACTIVITY, hours: '2:00' },
      ],
    },
  ],
  /** The booking being made: an hour and a half, corrected to two, on the running annual accounts. */
  booking: {
    day: 16,
    nr: '10010',
    activity: YEAR_ACTIVITIES[0].label,
    /** What the amount field reads while typing, and after the correction. */
    typed: '01:30',
    corrected: '02:00',
    /** The same amount as the day list writes it, beside the other entries. */
    hours: '2:00',
  },
  /** The month counter in the header, before and after that booking. */
  monthBefore: '64:00',
  monthAfter: '66:00',
  /** The week strip above the form: the working week, plus the weekend it has nothing on. */
  weekDays: [6, 7, 8, 9, 10, 11, 12],
  /** The day that strip has selected. */
  selectedDay: 10,
  /** The step buttons under the amount — the second is the one the animation taps. */
  quickSteps: ['+0:15', '+0:30', '+1:00', '+8:00'],
} as const

/**
 * The day a monthly return is due: the last day of the month it covers. The visuals are set in a
 * 31-day month, and the assistant quotes this rather than a date of its own.
 */
export const MONTH_DEADLINE_DAY = 31

/**
 * The hours report: what was booked this month, on which client, and whether it has been invoiced.
 * The same clients and activity names the boards use, so a report row can be traced back to the
 * planning row it came from.
 */
export type ReportStatus = 'te-factureren' | 'gefactureerd' | 'niet-facturabel'

/**
 * The report the animation filters: one line per booking, with the rate it is billed at and where
 * it stands. `te-factureren` is work that is billable but not yet on an invoice — the selection the
 * card is about, because that is money the firm has earned and not yet asked for.
 */
export const HOURS_REPORT = [
  { day: 14, nr: '10010', activity: MONTH_ACTIVITIES[0].fullLabel, hours: 3, rate: 85, status: 'te-factureren' },
  { day: 14, nr: '10047', activity: REVIEW_ACTIVITY, hours: 1.5, rate: 0, status: 'niet-facturabel' },
  { day: 15, nr: '10018', activity: YEAR_ACTIVITIES[0].label, hours: 2.5, rate: 95, status: 'gefactureerd' },
  { day: 16, nr: '10044', activity: MONTH_ACTIVITIES[1].fullLabel, hours: 1.25, rate: 85, status: 'te-factureren' },
  { day: 17, nr: '10010', activity: YEAR_ACTIVITIES[0].label, hours: 4, rate: 95, status: 'te-factureren' },
  { day: 18, nr: '10001', activity: MONTH_ACTIVITIES[3].fullLabel, hours: 2, rate: 75, status: 'gefactureerd' },
  { day: 21, nr: '10040', activity: MONTH_ACTIVITIES[0].fullLabel, hours: 1.75, rate: 85, status: 'te-factureren' },
  { day: 21, nr: '10020', activity: 'Intern overleg', hours: 1, rate: 0, status: 'niet-facturabel' },
] as const satisfies readonly {
  day: number
  nr: ClientNr
  activity: string
  hours: number
  rate: number
  status: ReportStatus
}[]

/** What a line is worth: its hours at its rate. Non-billable work is worth nothing, and says so. */
export const linePrice = (row: { hours: number; rate: number }) => row.hours * row.rate

/**
 * The report is a window on the month, so its header counts the whole month rather than the lines
 * on screen. Filtering on "te factureren" narrows all three figures at once — which is the point of
 * the card: what is still to be invoiced is one click away, in hours and in euros.
 */
export const HOURS_REPORT_TOTALS = {
  all: { results: 548, hours: '1412:45', amount: 121496.25 },
  toInvoice: { results: 164, hours: '418:30', amount: 35991.5 },
} as const

/** "DONDERDAG 9 JULI" — the header the phone puts above one day's entries. */
export function phoneDayLabel(day: number): string {
  return `${day}`
}

export const HOURS_WEEK_DAYS = ['MA', 'DI', 'WO', 'DO', 'VR'] as const
export const HOURS_WEEKS = [
  [6, 7, 8, 9, 10],
  [13, 14, 15, 16, 17],
  [20, 21, 22, 23, 24],
] as const

/**
 * Which weekday a day in the visuals' month falls on, as an index into a Monday-first week. The
 * name itself lives in `visual-copy.tsx`, so it can be written in the language of the page.
 */
export function weekdayIndexOf(day: number): number {
  const sundayFirst = new Date(2026, 6, day).getDay()
  return (sundayFirst + 6) % 7
}

/* ------------------------------------------------------------- budgets */

/**
 * Fee budgets per client, and how far this year's work has eaten into them. Two clients run over —
 * which is a fact the AI assistant states out loud, so it is derived from here rather than restated.
 */
export type ClientBudget = {
  nr: ClientNr
  /** The agreed fee budget in euros, as a number: what is spent against it is derived from it. */
  budget: number
  /** Hours written on the client this book year. */
  hours: string
  pct: number
  overrunPct?: number
}

export const CLIENT_BUDGETS: readonly ClientBudget[] = [
  { nr: '10047', budget: 3900, hours: '46:00', pct: 62 },
  { nr: '10027', budget: 8200, hours: '82:15', pct: 74 },
  { nr: '10044', budget: 7000, hours: '76:30', pct: 58 },
  { nr: '10052', budget: 3900, hours: '38:00', pct: 71, overrunPct: 101 },
  { nr: '10020', budget: 5250, hours: '51:45', pct: 83, overrunPct: 108 },
  // Not over, but close enough that the report flags it before it is — which is the whole point of
  // watching a budget rather than reading it afterwards.
  { nr: '10010', budget: 6400, hours: '64:30', pct: 91 },
  { nr: '10014', budget: 4750, hours: '41:15', pct: 66 },
]

/** "€ 3.900" — euros the way the app writes them, without leaning on a locale at render time. */
export function euro(amount: number, cents = false): string {
  const whole = Math.floor(amount)
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  if (!cents) return `€ ${grouped}`
  return `€ ${grouped},${Math.round((amount - whole) * 100)
    .toString()
    .padStart(2, '0')}`
}

/** The clients over their fee budget, worst first — what the AI chat reports. */
export const OVER_BUDGET = CLIENT_BUDGETS.filter((row) => row.overrunPct !== undefined).sort(
  (a, b) => (b.overrunPct ?? 0) - (a.overrunPct ?? 0),
)

/* ---------------------------------------------------------- lead times */

/** Work is flagged once it has been open longer than this. */
export const LEAD_TIME_SIGNAL_DAYS = 90

/**
 * Longest-open work, in days. The lead-time list ranks these and the dashboard counts how many are
 * over the signal threshold — both off this one list, so the dashboard's badge cannot claim a
 * different number than the list shows.
 */
export type LeadTime = {
  nr: ClientNr
  /** Full activity name, for the places with room to spell it out. */
  activity: string
  /** Compact label for the signal list, where four columns share one card. */
  short: string
  /** Book year the work belongs to — a filing is always a filing *over* a year. */
  year: number
  days: number
  status: string
}

/**
 * Longest-open work, descending. Every activity matches its client's legal form: only the
 * eenmanszaak files inkomstenbelasting, the BVs and holdings file vennootschapsbelasting, and the
 * suppletie is over an older year, which is exactly why it has been open the longest.
 */
export const LEAD_TIMES: readonly LeadTime[] = [
  {
    nr: '10010',
    activity: 'Suppletie omzetbelasting',
    short: 'Suppletie OB',
    year: 2024,
    days: 112,
    status: 'Ter goedkeuring klant',
  },
  {
    nr: '10035',
    activity: 'Jaarrekening',
    short: 'Jaarrekening',
    year: 2025,
    days: 94,
    status: workflowStatus('controle').label,
  },
  {
    nr: '10020',
    activity: 'Vennootschapsbelasting (VPB)',
    short: 'VPB-aangifte',
    year: 2025,
    days: 78,
    status: 'Wacht op klant',
  },
  {
    nr: '10021',
    activity: 'Inkomstenbelasting (IB)',
    short: 'IB-aangifte',
    year: 2025,
    days: 46,
    status: 'Wacht op stukken',
  },
  {
    nr: '10024',
    activity: 'Publicatiestukken KvK',
    short: 'Publicatiestukken',
    year: 2025,
    days: 39,
    status: workflowStatus('uitvoering').label,
  },
]

export const OVERDUE_COUNT = LEAD_TIMES.filter((row) => row.days > LEAD_TIME_SIGNAL_DAYS).length

export type MyTask = {
  /** What has to be done. Kept separate from the client so no client name is typed out by hand. */
  activity: string
  /** The client it belongs to, where it is about one. */
  nr?: ClientNr
  /** Subtasks ticked off, of the total. */
  done: number
  total: number
}

/**
 * The open items on the reviewing accountant's own list, in the order he would work them: the Q2
 * VAT return is due at the end of July and ready to file, the annual accounts are with him for
 * review, and the oldest dossier on the lead-time list needs the client chased for approval.
 */
export const MY_TASKS: readonly MyTask[] = [
  { activity: 'Btw-aangifte Q2 voorbereiden', done: 5, total: 5 },
  { activity: 'Jaarrekening reviewen', nr: '10002', done: 2, total: 6 },
  { activity: 'Suppletie OB nabellen', nr: '10010', done: 0, total: 3 },
]

/* --------------------------------------------------------- one client's dossier */

/**
 * The client whose dossier the Klantbeheer hero opens. The same client the record card on the home
 * page shows, so a visitor arriving from there opens the file they were just looking at.
 */
export const DOSSIER_NR = '10010' as const

export type AgreementLine = {
  /** What is done. */
  label: string
  /** "Maandelijks", or the month a yearly obligation falls in. */
  whenKey: 'monthly' | MonthKey
  member: TeamInitials
  /** Budgeted duration, in minutes, so it prints as the app prints it. */
  minutes: number
  price: number
}

/** Months a yearly obligation can be pinned to, as a key each language writes its own way. */
export type MonthKey = 'jan' | 'sep' | 'okt'

/**
 * What this client has agreed to have done, and on what footing: the recurring work, who does it,
 * how long it is budgeted for and what it costs.
 *
 * The two monthly lines are the client's own month planning — the same activities, handled by the
 * same colleagues the month board puts against this client — so the dossier and the board cannot
 * disagree about who does its bookkeeping. The yearly lines come off the shared year activities.
 */
export const CLIENT_AGREEMENT: readonly AgreementLine[] = [
  {
    label: MONTH_ACTIVITIES[0].fullLabel,
    whenKey: 'monthly',
    member: monthAssignee(DOSSIER_NR, 'administratie').initials as TeamInitials,
    minutes: 105,
    price: 115,
  },
  {
    label: MONTH_ACTIVITIES[1].fullLabel,
    whenKey: 'monthly',
    member: monthAssignee(DOSSIER_NR, 'omzetbelasting').initials as TeamInitials,
    minutes: 45,
    price: 85,
  },
  {
    label: MONTH_ACTIVITIES[2].fullLabel,
    whenKey: 'monthly',
    member: monthAssignee(DOSSIER_NR, 'salaris').initials as TeamInitials,
    minutes: 120,
    price: 145,
  },
  {
    label: MONTH_ACTIVITIES[3].fullLabel,
    whenKey: 'monthly',
    member: monthAssignee(DOSSIER_NR, 'loonheffingen').initials as TeamInitials,
    minutes: 35,
    price: 65,
  },
  { label: YEAR_ACTIVITIES[0].label, whenKey: 'sep', member: 'MV', minutes: 360, price: 650 },
  {
    // "VPB-aangifte", as the lead-time list writes it. The full "Vennootschapsbelasting (VPB)" is
    // wider than the column it sits in here and ended in an ellipsis mid-word.
    label: 'VPB-aangifte',
    whenKey: 'okt',
    member: 'AS',
    minutes: 150,
    price: 275,
  },
  {
    label: YEAR_ACTIVITIES[2].fullLabel ?? YEAR_ACTIVITIES[2].label,
    whenKey: 'jan',
    member: 'TV',
    minutes: 45,
    price: 85,
  },
]

/**
 * What the recurring arrangement comes to in a year: the monthly lines twelve times over, the yearly
 * ones once. Summing the five prices as they stand would say € 1.210, which is neither a month nor a
 * year and answers no question anyone has.
 */
export const CLIENT_AGREEMENT_PER_YEAR = CLIENT_AGREEMENT.reduce(
  (sum, line) => sum + line.price * (line.whenKey === 'monthly' ? 12 : 1),
  0,
)

/**
 * Who booked hours on this client. The dossier draws this as a chart; at this size a share per
 * colleague reads better, and it says the same thing — more than one person works this file.
 */
export const DOSSIER_HOURS_BY_MEMBER: readonly { member: TeamInitials; hours: string; pct: number }[] = [
  { member: 'HM', hours: '28:15', pct: 44 },
  { member: 'NK', hours: '19:30', pct: 30 },
  { member: 'YE', hours: '16:45', pct: 26 },
]

/**
 * This client's hours, split the way the dossier splits them. The total is the budget list's own
 * figure for this client, so the two cannot report different hours for the same file.
 */
const DOSSIER_BUDGET = CLIENT_BUDGETS.find((row) => row.nr === DOSSIER_NR)

export const DOSSIER_HOURS = {
  /** Everything booked on this client this year. */
  total: DOSSIER_BUDGET?.hours ?? '64:30',
  /** The share that can be invoiced, and what it comes to. */
  billable: '44:45',
  billableAmount: 3695,
  nonBillable: '19:45',
  budget: DOSSIER_BUDGET?.budget ?? 6400,
  /** How much of the fee budget the work has used — the same percentage the budget card shows. */
  usedPct: DOSSIER_BUDGET?.pct ?? 91,
} as const

/** Open work on this client, with how far each item has got. */
export const DOSSIER_TASKS: readonly { activity: string; done: number; total: number }[] = [
  { activity: 'Suppletie omzetbelasting nabellen', done: 0, total: 3 },
  { activity: 'Jaarrekening 2025 reviewen', done: 2, total: 6 },
  { activity: 'Stukken opvragen bij de klant', done: 1, total: 4 },
  { activity: 'Aansluitcontrole loonheffingen', done: 3, total: 3 },
  { activity: 'KvK-publicatie deponeren', done: 2, total: 2 },
]

/* --------------------------------------------------- derived AI answers */

/**
 * What the assistant is asked about, computed from the boards rather than written out. An answer
 * that names clients is a claim about another visual on the same page, so it has to be derived —
 * these lists were previously hand-written and named clients whose boards said otherwise.
 */
const cellState = (cell: PlanningCell) => (typeof cell === 'string' ? cell : cell.from)

/** Year work that is not finished yet — the year board's own definition of "loopt nog". */
export const UNFINISHED_YEARWORK = YEAR_PLANNING.filter((row) => {
  const state = cellState(row.cells[0])
  return state !== 'klaar' && state !== 'leeg'
}).map((row) => ({ nr: row.nr, state: cellState(row.cells[0]) }))

/** Clients whose VAT return for this month has not been picked up yet. */
export const UNSTARTED_VAT = MONTH_PLANNING.filter((row) => row.cells[1] === 'gepland').map((row) => row.nr)

/** Occupancy for one row of the capacity chart; `spikesTo` is the value it rises to mid-loop. */
export type CapacityRow = { member: TeamInitials; pct: number; spikesTo?: number }

/**
 * Occupancy per colleague, busiest first — mirrors the capacity chart.
 *
 * The whole team is listed, not a selection: 812 clients spread over six people would put 135
 * clients on each desk, which is not a practice anyone would recognise. Eleven is the size the
 * client base implies, and it is the number the chart has to show for the ratio to read right.
 */
export const CAPACITY: readonly CapacityRow[] = [
  { member: 'HM', pct: 88 },
  { member: 'LD', pct: 86 },
  { member: 'AS', pct: 84 },
  { member: 'DA', pct: 82, spikesTo: 96 },
  { member: 'NK', pct: 79 },
  { member: 'BJ', pct: 74 },
  { member: 'TV', pct: 68 },
  { member: 'SV', pct: 65 },
  { member: 'YE', pct: 62 },
  { member: 'MV', pct: 60 },
  { member: 'ID', pct: 57 },
]

/** Who the assistant can honestly say has room — after any mid-loop spike is applied. */
export const HAS_ROOM = CAPACITY.filter((row) => (row.spikesTo ?? row.pct) < 75).sort((a, b) => a.pct - b.pct)

/** `monthIndex` is 0-based, into the language's own month names. */
export type CapacityMonth = { monthIndex: number; pct: number; spikesTo?: number }

/**
 * Occupancy per calendar month across the running book year — the second half of the capacity view.
 *
 * The busy months are the ones the compliance calendar actually loads up: January carries the
 * year-end close of the previous book year, April and May the income-tax season, and the load
 * climbs again towards the filing deadlines at the end of the year. The summer is genuinely quiet,
 * which is the whole point of looking a year ahead.
 */
export const CAPACITY_MONTHS: readonly CapacityMonth[] = [
  { monthIndex: 0, pct: 90 },
  { monthIndex: 1, pct: 49 },
  { monthIndex: 2, pct: 55 },
  { monthIndex: 3, pct: 83 },
  { monthIndex: 4, pct: 92, spikesTo: 124 },
  // The quiet months. Deep enough that the free half of the bar is a place a word can be written —
  // at 59% a phone's track leaves under 50px, which "ruimte" did not fit in — and true of a firm's
  // summer besides.
  { monthIndex: 5, pct: 38 },
  { monthIndex: 6, pct: 36 },
  { monthIndex: 7, pct: 52 },
  { monthIndex: 8, pct: 68 },
  { monthIndex: 9, pct: 77 },
  { monthIndex: 10, pct: 84 },
  { monthIndex: 11, pct: 91 },
]

/**
 * Total hours planned across the book year, behind the capacity header. It has to hold against the
 * rest of the universe: 812 clients at roughly 23 hours of recurring work each, spread over the
 * eleven colleagues in `CAPACITY`. A bare number, so each language groups and writes it its own way.
 */
export const CAPACITY_PLANNED_HOURS = 18640

/* --------------------------------------------------------------- assistant */

/**
 * What the assistant is asked on each page. The answers are composed where the numbers live, from
 * the data above — this only fixes the questions, so the three chat strips ask about the boards the
 * same page shows rather than about invented work.
 */
export const AI_CHAT = {
  /** Home + planning: the year board's open work, then the file that has been still the longest. */
  yearWork: {
    question: 'Welke jaarrekeningen lopen nog?',
    followUp: 'En wat ligt het langst stil?',
  },
  /** Planning page: this month's unstarted VAT returns, then who can pick them up. */
  planning: {
    question: 'Welke btw-aangiftes zijn nog niet gestart?',
    followUp: 'En wie heeft er deze week nog ruimte?',
  },
  /** Time tracking: one client's hours last month, then who is running over budget. */
  hours: {
    subject: '10010',
    question: `Hoeveel uur schreef het team vorige maand op ${client('10010').short}?`,
    followUp: 'Welke klanten zitten boven hun budget?',
    /** The month before the one the site is set in, and what was booked on that client in it. */
    lastMonthLabel: 'juni',
    lastMonthHours: '126:30',
    lastMonthBillable: '112:00',
  },
} as const
